<template>
  <slot v-if="!isGlobalProxyMode" :items="filteredList" />
  <Component
    :is="DraggableList"
    v-if="DraggableList && isEditing && canEdit && !isInReusable && entity"
    :list="filteredList"
    :name="name"
    :entity="entity"
    :field-key="fieldKey"
    :allowed-fragments="allowedFragments"
    :nesting-level="nestingLevel"
    :drop-alignment="dropAlignment"
    :field-list-type="fieldListType"
    :class="[
  attrs.class,
  listClass,
  editClass,
  { [nonEmptyClass]: filteredList.length }
]"
    :is-nested="isNested"
    :language="providerEntity?.language"
    :proxy-mode="proxyMode"
    :tag="tag"
    :global-proxy-mode="!!isGlobalProxyMode"
    :should-render-item="shouldRenderItem"
  />
  <component
    :is="tag"
    v-else-if="
  !editOnly && (filteredList.length || isEditing || isPreview) && !proxyMode && !isGlobalProxyMode
"
    :class="[
  attrs.class,
  {
    [nonEmptyClass]: filteredList.length
  },
  listClass
]"
  >
    <BlokkliItem
      v-for="(item, i) in filteredList"
      :key="item.uuid + fieldListType"
      v-bind="item"
      :parent-type="isNested ? entity?.bundle : ''"
      :data-uuid="isPreview ? item.uuid : void 0"
      :index="i"
    />
  </component>
  <slot v-if="!isGlobalProxyMode" name="after" :items="filteredList" />
</template>

<script setup>
import {
  computed,
  useAttrs,
  inject,
  provide,
  ref
} from "#imports";
import { isVisibleByOptions } from "#blokkli/helpers/runtimeHelpers";
import BlokkliItem from "./BlokkliItem.vue";
import {
  INJECT_ENTITY_CONTEXT,
  INJECT_FIELD_LIST_TYPE,
  INJECT_IS_EDITING,
  INJECT_IS_IN_REUSABLE,
  INJECT_IS_NESTED,
  INJECT_IS_PREVIEW,
  INJECT_NESTING_LEVEL,
  INJECT_FIELD_LIST_BLOCKS,
  INJECT_PROVIDER_BLOCKS,
  INJECT_EDIT_CONTEXT,
  INJECT_MUTATED_FIELDS_MAP,
  INJECT_EDIT_FIELD_LIST_COMPONENT,
  INJECT_PROVIDER_CONTEXT,
  INJECT_FIELD_PROXY_MODE,
  INJECT_GLOBAL_PROXY_MODE,
  INJECT_FIELD_USES_PROXY
} from "../helpers/symbols";
if (import.meta.hot) {
  import.meta.hot.accept("#blokkli/helpers/runtimeHelpers", () => {
  });
}
const DraggableList = inject(
  INJECT_EDIT_FIELD_LIST_COMPONENT,
  null
);
const attrs = useAttrs();
defineOptions({
  inheritAttrs: false
});
defineSlots();
const isEditing = inject(INJECT_IS_EDITING, false);
const isGlobalProxyMode = inject(
  INJECT_GLOBAL_PROXY_MODE,
  null
);
const isInReusable = inject(INJECT_IS_IN_REUSABLE, false);
const isPreview = inject(INJECT_IS_PREVIEW, null);
const isNested = inject(INJECT_IS_NESTED, false);
const nestingLevel = inject(INJECT_NESTING_LEVEL, 0);
const mutatedFields = inject(
  INJECT_MUTATED_FIELDS_MAP,
  null
);
const editContext = inject(INJECT_EDIT_CONTEXT, null);
const entity = inject(INJECT_ENTITY_CONTEXT);
if (!entity) {
  throw new Error("Missing entity context.");
}
const providerEntity = inject(
  INJECT_PROVIDER_CONTEXT
);
if (!providerEntity) {
  throw new Error(
    "Missing bl\xF6kkli injection: " + INJECT_PROVIDER_CONTEXT.toString()
  );
}
const props = defineProps({
  name: { type: String, required: true },
  list: { type: [Array, Object, null], required: false, default: () => [] },
  tag: { type: String, required: false, default: "div" },
  fieldListType: { type: null, required: false, default: "default" },
  editOnly: { type: Boolean, required: false },
  listClass: { type: String, required: false, default: "" },
  editClass: { type: String, required: false, default: "" },
  nonEmptyClass: { type: String, required: false, default: "" },
  allowedFragments: { type: Array, required: false, default: () => [] },
  dropAlignment: { type: String, required: false, default: void 0 },
  proxyMode: { type: Boolean, required: false },
  shouldRenderItem: { type: Function, required: false, default: void 0 }
});
const canEdit = ref(true);
const fieldKey = computed(() => {
  if (canEdit.value) {
    return entity.uuid + ":" + props.name;
  }
  return void 0;
});
const fieldListType = computed(() => props.fieldListType);
function filterVisible(item) {
  if (!item) {
    return false;
  }
  if (isEditing) {
    return true;
  }
  const isVisible = isVisibleByOptions(item, providerEntity.value.language);
  const isVisibleCustom = props.shouldRenderItem ? props.shouldRenderItem(item) : true;
  return isVisible && isVisibleCustom;
}
const filteredList = computed(() => {
  if (mutatedFields && !isInReusable && editContext && fieldKey.value && (isPreview?.value || isEditing)) {
    return ((mutatedFields[fieldKey.value] || {}).list || []).map((v) => {
      const mutatedOptions = editContext.mutatedOptions[v.uuid] || {};
      return {
        ...v,
        options: {
          ...v.options,
          ...mutatedOptions
        }
      };
    });
  }
  const list = Array.isArray(props.list) ? props.list : [props.list];
  return list.filter(filterVisible);
});
provide(INJECT_IS_NESTED, true);
provide(INJECT_NESTING_LEVEL, nestingLevel + 1);
provide(INJECT_FIELD_LIST_TYPE, fieldListType);
provide(INJECT_FIELD_LIST_BLOCKS, filteredList);
if (props.proxyMode) {
  provide(INJECT_IS_EDITING, false);
  provide(INJECT_FIELD_PROXY_MODE, false);
  provide(INJECT_FIELD_USES_PROXY, true);
}
if (!isNested) {
  provide(INJECT_PROVIDER_BLOCKS, filteredList);
}
</script>
